import { useState, type FormEvent } from 'react';

// material-ui
import Alert from '@mui/material/Alert';
import Button from '@mui/material/Button';
import CircularProgress from '@mui/material/CircularProgress';
import Dialog from '@mui/material/Dialog';
import DialogActions from '@mui/material/DialogActions';
import DialogContent from '@mui/material/DialogContent';
import DialogContentText from '@mui/material/DialogContentText';
import DialogTitle from '@mui/material/DialogTitle';
import Stack from '@mui/material/Stack';
import TextField from '@mui/material/TextField';

// project imports
import { useAuth } from 'contexts/AuthContext';
import { podeRenovarPeloEntra } from 'modules/auth/services/renovacaoSessao';

// Sessão encerrada no meio do uso: o servidor recusou o token e a sessão não renovou sozinha. A
// página fica onde está; as chamadas que receberam 401 esperam este diálogo e são repetidas depois
// que a pessoa entra de novo (popup do Entra, ou a senha para quem usa login local). "Sair" leva ao
// login guardando a rota.
export default function SessaoEncerradaDialog() {
  const { sessaoEncerrada, user, continuarConectado, reentrarComSenha, logout } = useAuth();
  const [senha, setSenha] = useState('');
  const [enviando, setEnviando] = useState(false);
  const [erro, setErro] = useState<string | null>(null);

  if (!sessaoEncerrada) return null;

  const viaMicrosoft = podeRenovarPeloEntra();

  const executar = (acao: Promise<void>) => {
    setErro(null);
    setEnviando(true);
    acao
      .then(() => setSenha(''))
      .catch((e) => setErro(e instanceof Error ? e.message : 'Não deu para entrar de novo.'))
      .finally(() => setEnviando(false));
  };

  // Nada de await antes de continuarConectado: o Safari só abre o popup durante o clique.
  const entrarComMicrosoft = () => executar(continuarConectado());

  const entrarComSenha = (e: FormEvent) => {
    e.preventDefault();
    if (senha) executar(reentrarComSenha(senha));
  };

  const carregando = enviando ? <CircularProgress size={16} color="inherit" /> : null;

  return (
    <Dialog open maxWidth="xs" fullWidth disableEscapeKeyDown aria-labelledby="sessao-encerrada-titulo">
      <form onSubmit={entrarComSenha}>
        <DialogTitle id="sessao-encerrada-titulo">Sua sessão acabou</DialogTitle>
        <DialogContent>
          <Stack sx={{ gap: 2 }}>
            <DialogContentText>
              Entre de novo para continuar de onde parou. O que você estava fazendo continua na tela e é concluído assim que você entrar.
            </DialogContentText>
            {!viaMicrosoft && (
              <>
                <TextField label="E-mail" value={user?.email ?? ''} disabled fullWidth size="small" />
                <TextField
                  label="Senha"
                  type="password"
                  value={senha}
                  onChange={(e) => setSenha(e.target.value)}
                  autoComplete="current-password"
                  autoFocus
                  fullWidth
                  size="small"
                />
              </>
            )}
            {erro && <Alert severity="error">{erro}</Alert>}
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button color="secondary" onClick={logout} disabled={enviando}>
            Sair
          </Button>
          {viaMicrosoft ? (
            <Button variant="contained" onClick={entrarComMicrosoft} disabled={enviando} startIcon={carregando}>
              {enviando ? 'Conclua na janela da Microsoft...' : 'Entrar com Microsoft'}
            </Button>
          ) : (
            <Button type="submit" variant="contained" disabled={enviando || !senha} startIcon={carregando}>
              Entrar
            </Button>
          )}
        </DialogActions>
      </form>
    </Dialog>
  );
}
